import { useState, useEffect, useRef } from 'react';
import { AISummaryService } from './AISummaryService';
import { IAISummaryData, IContainerCache } from './AISummary.types';
import { Logger } from '../../../../../logger/src';

// Module-level cache: shared across all hook instances, survives re-renders
const summaryCache = new Map<string, IContainerCache>();

const MAX_CACHE_ENTRIES = 50;

function setCacheEntry(key: string, value: IContainerCache) {
  if (summaryCache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = summaryCache.keys().next().value;
    if (oldestKey) summaryCache.delete(oldestKey);
  }
  summaryCache.set(key, value);
}

function getCacheEntry(key: string): IContainerCache | undefined {
  const entry = summaryCache.get(key);
  if (entry) {
    // Move to end (most recently used)
    summaryCache.delete(key);
    summaryCache.set(key, entry);
  }
  return entry;
}

export function useAISummary(containerId: string | undefined) {
  // Initialize state from cache synchronously to avoid a blank frame on remount
  const [summaryData, setSummaryData] = useState<IAISummaryData | null>(() => {
    if (!containerId) return null;
    return getCacheEntry(containerId)?.summaryData ?? null;
  });
  const [loading, setLoading] = useState(() => {
    if (!containerId) return false;
    // If cache has data, no loading needed; otherwise start loading
    return !getCacheEntry(containerId)?.summaryData;
  });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!containerId) {
      setSummaryData(null);
      setLoading(false);
      return;
    }

    // Check cache first
    const cached = getCacheEntry(containerId);
    if (cached?.summaryData) {
      setSummaryData(cached.summaryData);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        Logger.debug(`[AISummary] useAISummary hook started for containerId: ${containerId}`);
        const service = AISummaryService.getInstance();

        // Step 1: Resolve container
        Logger.debug(`[AISummary] Step 1: Fetching container info for ${containerId}`);
        const containerInfo = await service.getContainer(containerId);
        if (cancelled || !mountedRef.current) {
          Logger.debug(`[AISummary] Component unmounted or cancelled after getContainer`);
          return;
        }

        // Check if summary is available (mock returns 'Active', real API returns 'GENERATED')
        const status = containerInfo.summaryData?.status;
        Logger.debug(`[AISummary] Container status: ${status}`);
        if (status !== 'Active' && status !== 'GENERATED') {
          Logger.info(`[AISummary] Summary unavailable - status is ${status}`);
          setSummaryData({
            shortNote: '',
            note: '',
            actionItems: [],
            transcriptUrl: '',
            status: 'unavailable',
          });
          setLoading(false);
          return;
        }

        // Step 2: Fetch summary content
        Logger.debug(`[AISummary] Step 2: Fetching summary content for ${containerId}`);
        const data = await service.getSummary(containerInfo);
        if (cancelled || !mountedRef.current) {
          Logger.debug(`[AISummary] Component unmounted or cancelled after getSummary`);
          return;
        }

        // Cache result
        Logger.debug(`[AISummary] Caching and setting summary data for ${containerId}`);
        setCacheEntry(containerId, { containerInfo, summaryData: data });
        setSummaryData(data);
        setLoading(false);
        Logger.debug(`[AISummary] Successfully loaded summary for ${containerId}`);
      } catch (error) {
        if (cancelled || !mountedRef.current) {
          Logger.debug(`[AISummary] Component unmounted or cancelled after error`);
          return;
        }
        Logger.info(`[AISummary] Error in useAISummary for ${containerId}: ${error}`);
        setSummaryData({
          shortNote: '',
          note: '',
          actionItems: [],
          transcriptUrl: '',
          status: 'error',
          error: (error as Error).message,
        });
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [containerId]);

  return { summaryData, loading };
}
